import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, UserCog, UserMinus, Crown } from 'lucide-react'
import type { TeamMember } from '@/hooks/useTeam'
import { RemoveMemberModal } from './RemoveMemberModal'
import { ChangeRoleModal } from './ChangeRoleModal'
import { TransferOwnershipModal } from './TransferOwnershipModal'

interface MemberActionsMenuProps {
  member: TeamMember
  isOwner: boolean
  canManageTeam: boolean
  currentUserRole: 'owner' | 'admin' | 'member'
}

export function MemberActionsMenu({ 
  member, 
  isOwner, 
  canManageTeam,
  currentUserRole 
}: MemberActionsMenuProps) {
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)

  // Don't show menu if user is current user
  if (member.is_current_user) {
    return null
  }

  // Don't show menu if user doesn't have permission
  if (!canManageTeam) {
    return null
  }

  // Determine what actions are available
  const canChangeRole = () => {
    if (member.role === 'owner') return false
    if (currentUserRole === 'admin' && member.role === 'admin') return false
    return true
  }

  const canRemove = () => {
    if (member.role === 'owner') return false
    if (currentUserRole === 'admin' && member.role === 'admin') return false
    return true
  }

  const canTransferOwnership = () => {
    return isOwner && member.role !== 'owner'
  }

  const hasAnyActions = canChangeRole() || canRemove() || canTransferOwnership()

  if (!hasAnyActions) {
    return null
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {canChangeRole() && (
            <DropdownMenuItem onClick={() => setShowChangeRoleModal(true)}>
              <UserCog className="mr-2 h-4 w-4" />
              Change Role
            </DropdownMenuItem>
          )}
          
          {canTransferOwnership() && (
            <>
              {canChangeRole() && <DropdownMenuSeparator />}
              <DropdownMenuItem 
                onClick={() => setShowTransferModal(true)}
                className="text-amber-600"
              >
                <Crown className="mr-2 h-4 w-4" />
                Transfer Ownership
              </DropdownMenuItem>
            </>
          )}
          
          {canRemove() && (
            <>
              {(canChangeRole() || canTransferOwnership()) && <DropdownMenuSeparator />}
              <DropdownMenuItem 
                onClick={() => setShowRemoveModal(true)}
                className="text-destructive"
              >
                <UserMinus className="mr-2 h-4 w-4" />
                Remove Member
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Modals */}
      <RemoveMemberModal
        open={showRemoveModal}
        onOpenChange={setShowRemoveModal}
        member={member}
      />
      
      <ChangeRoleModal
        open={showChangeRoleModal}
        onOpenChange={setShowChangeRoleModal}
        member={member}
      />
      
      <TransferOwnershipModal
        open={showTransferModal}
        onOpenChange={setShowTransferModal}
        member={member}
      />
    </>
  )
}
